import { useState, useEffect } from "react";
import { CheckCircle2, AlertTriangle, Info } from "lucide-react";
import Select from "../Select/Select";
import "./PaymentForm.scss";

// ─── Konštanty ────────────────────────────────────────────────────────────────
export const PAYMENT_TYPE_OPTIONS_NEW = [
  { value: "CASH",          label: "Hotovosť (CASH)" },
  { value: "CARD",          label: "Platba kartou (CARD)" },
  { value: "INVOICE",       label: "Faktúra (INVOICE)" },
  { value: "DEBT",          label: "Na dlh (DEBT)" },
  { value: "NOT_DELIVERED", label: "Nedoručené / Storno (NOT_DELIVERED)" },
];

export const PAYMENT_TYPE_OPTIONS_EDIT = [
  { value: "CASH",    label: "Hotovosť (CASH)" },
  { value: "CARD",    label: "Platba kartou (CARD)" },
  { value: "INVOICE", label: "Faktúra (INVOICE)" },
  { value: "DEBT",    label: "Na dlh (DEBT)" },
];

export const PAYMENT_TYPE_LABELS = {
  CASH:          "Hotovosť",
  CARD:          "Platba kartou",
  INVOICE:       "Faktúra",
  DEBT:          "Na dlh",
  NOT_DELIVERED: "Nedoručené",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
export function formatCurrency(amount) {
  return Number(amount || 0)
    .toFixed(2)
    .replace(".", ",") + " €";
}

export function toNumber(val) {
  const n = parseFloat(String(val).replace(",", "."));
  return isNaN(n) ? 0 : n;
}

/**
 * Prisne precitanie sumy z policka pred odoslanim. Na rozdiel od toNumber()
 * nevrati pri prazdnom alebo nezmyselnom vstupe 0 -- inak by sa hotovostna
 * objednavka ticho ulozila ako zaplatene 0 € a cela by spadla do dlhu.
 * @returns {number|null} suma, alebo null ak nie je platna (prazdna, zaporna, text)
 */
export function parseAmountInput(val) {
  const text = String(val ?? "").trim().replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  return Math.round(parseFloat(text) * 100) / 100;
}

/**
 * Suma, ktoru zakaznik pri doruceni realne dal, odvodena z ulozenej objednavky.
 * Zrkadli deriveReceivedAmount() v backend/services/paymentCalc.js.
 *
 * Pri CASH s preplatkom je amount_paid orezany na cenu objednavky a splatka
 * dlhu sedi v kladnom balance. Predvyplnit opravu len hodnotou amount_paid
 * by pri ulozeni bez zmeny tu splatku ticho zrusila (10 € za obed za 6 € ->
 * odoslalo by sa 6 € a 4 € by sa zakaznikovi vratili do dlhu).
 */
export function deriveReceivedAmount(order) {
  const amountPaid = toNumber(order?.amount_paid);
  const balance = toNumber(order?.balance);
  const total = toNumber(order?.total_price);

  if (total === 0) return amountPaid;
  if (String(order?.payment_type || "").toUpperCase() === "CASH") {
    return Math.round((amountPaid + Math.max(balance, 0)) * 100) / 100;
  }
  return amountPaid;
}

/**
 * Vypočíta amount_paid a balance podľa pravidiel
 * pre zobrazenie feedbacku vodiča pred odoslaním.
 */
export function computePaymentFields(paymentType, receivedAmount, totalPrice) {
  const received = toNumber(receivedAmount);
  const total    = toNumber(totalPrice);

  switch (paymentType) {
    case "DEBT":
      return { amountPaid: 0, balance: -total };
    case "INVOICE":
      return { amountPaid: total, balance: 0 };
    case "NOT_DELIVERED":
      return { amountPaid: 0, balance: 0 };
    case "CARD":
      return { amountPaid: total, balance: 0 };
    case "CASH":
    default:
      if (total === 0) {
        // Samostatné vyplatenie dlhu
        return { amountPaid: received, balance: received };
      }
      return {
        amountPaid: Math.min(received, total),
        balance:    parseFloat((received - total).toFixed(2)),
      };
  }
}

// ─── Feedback komponent ────────────────────────────────────────────────────────
function PaymentFeedback({ paymentType, balance, amountPaid, totalPrice, customerSaldo }) {
  const received    = toNumber(amountPaid);
  const total       = toNumber(totalPrice);
  const saldo       = toNumber(customerSaldo);
  const hasSaldoDebt = saldo < 0;
  if (paymentType === "CARD") {
    return (
      <div className="pf__feedback pf__feedback--info">
        <Info size={16} />
        <span>Platba kartou – čiastka <strong>{formatCurrency(total)}</strong> bude zaplatená bezhotovostne.</span>
      </div>
    );
  }
  if (paymentType === "INVOICE") {
    return (
      <div className="pf__feedback pf__feedback--info">
        <Info size={16} />
        <span>Platba na faktúru – bez hotovostného vplyvu na pokladňu.</span>
      </div>
    );
  }

  if (paymentType === "NOT_DELIVERED") {
    return (
      <div className="pf__feedback pf__feedback--warning">
        <AlertTriangle size={16} />
        <span>Objednávka bude <strong>stornovaná</strong> – bez platby, nezapočíta sa do tržby.</span>
      </div>
    );
  }

  if (paymentType === "DEBT") {
    return (
      <div className="pf__feedback pf__feedback--debt">
        <AlertTriangle size={16} />
        <span>
          Celá suma <strong>{formatCurrency(total)}</strong> bude zaznamenaná ako dlh zákazníka.
        </span>
      </div>
    );
  }

  // CASH
  if (total === 0) {
    // Samostatné vyplatenie dlhu
    if (!hasSaldoDebt) {
      return (
        <div className="pf__feedback pf__feedback--error">
          <AlertTriangle size={16} />
          <span>Zákazník nemá evidovaný žiadny dlh na vyplatenie.</span>
        </div>
      );
    }
    if (received > Math.abs(saldo) + 0.001) {
      return (
        <div className="pf__feedback pf__feedback--error">
          <AlertTriangle size={16} />
          <span>
            Zaplatená suma prevyšuje aktuálny dlh zákazníka ({formatCurrency(Math.abs(saldo))}).
          </span>
        </div>
      );
    }
    if (received <= 0) {
      return (
        <div className="pf__feedback pf__feedback--warning">
          <AlertTriangle size={16} />
          <span>Zadaj sumu, ktorú zákazník platí na dlh.</span>
        </div>
      );
    }
    return (
      <div className="pf__feedback pf__feedback--success">
        <CheckCircle2 size={16} />
        <span>
          Zákazník platí <strong>{formatCurrency(received)}</strong> na dlh.
          Zostatok dlhu po platbe: <strong>{formatCurrency(Math.abs(saldo) - received)}</strong>
        </span>
      </div>
    );
  }

  // Štandardná CASH platba
  if (balance === 0) {
    return (
      <div className="pf__feedback pf__feedback--success">
        <CheckCircle2 size={16} />
        <span>Plná platba – bez zmeny salda zákazníka.</span>
      </div>
    );
  }

  if (balance > 0) {
    if (!hasSaldoDebt) {
      return (
        <div className="pf__feedback pf__feedback--error">
          <AlertTriangle size={16} />
          <span>
            Zákazník nemá evidovaný dlh. Suma prevyšuje cenu objednávky
            o <strong>{formatCurrency(balance)}</strong> – platbu nie je možné potvrdiť.
          </span>
        </div>
      );
    }
    return (
      <div className="pf__feedback pf__feedback--success">
        <CheckCircle2 size={16} />
        <span>
          Zákazník zaplatil o <strong>{formatCurrency(balance)}</strong> navyše.
          Dlh zákazníka sa zníži o <strong>{formatCurrency(Math.min(balance, Math.abs(saldo)))}</strong>.
        </span>
      </div>
    );
  }

  // balance < 0
  return (
    <div className="pf__feedback pf__feedback--debt">
      <AlertTriangle size={16} />
      <span>
        Zákazník zaplatil o <strong>{formatCurrency(Math.abs(balance))}</strong> menej.
        Tento rozdiel bude zaznamenaný ako <strong>dlh zákazníka</strong>.
      </span>
    </div>
  );
}

// ─── Hlavný komponent ─────────────────────────────────────────────────────────
function PaymentForm({
  paymentType,
  setPaymentType,
  amountPaid,
  setAmountPaid,
  order,
  isSubmitting,
  isEditMode = false, // true = upravujeme DELIVERED order, false = nový order
  lockPaymentType = false,
  lockedPaymentTypeHint = "",
  showDriverNote = false,
  driverNote,
  setDriverNote,
}) {
  const totalPrice = toNumber(order?.total_price);
  const customerSaldo = toNumber(order?.customer_saldo);

  // Resetuj formulár pri zmene order (stane sa zvonka cez prop)
  useEffect(() => {
    if (order) {
      if (isEditMode) {
        // V režime úpravy predvyplň existujúcimi hodnotami
        const resolvedPaymentType = lockPaymentType && order.default_payment_type
          ? order.default_payment_type
          : (order.payment_type || "CASH");
        setPaymentType(resolvedPaymentType);
        setAmountPaid(deriveReceivedAmount(order).toFixed(2));
      } else {
        // Nový order - štandardné predvyplnenie
        const resolvedPaymentType = lockPaymentType && order.default_payment_type
          ? order.default_payment_type
          : "CASH";
        setPaymentType(resolvedPaymentType);
        if (totalPrice === 0 && customerSaldo < 0) {
          setAmountPaid(String(Math.abs(customerSaldo).toFixed(2)));
        } else {
          setAmountPaid(String(totalPrice));
        }
      }
      if (showDriverNote) setDriverNote("");
    }
  }, [order, isEditMode, totalPrice, customerSaldo]);

  // Suma, ktorá k danému druhu platby patrí
  const defaultAmountForType = (type) => {
    if (type === "DEBT" || type === "NOT_DELIVERED") return "0";
    if (type === "INVOICE" || type === "CARD") return String(totalPrice);
    // CASH: pri vyplatení dlhu (cena 0) predvyplň výškou dlhu zákazníka
    if (totalPrice === 0 && customerSaldo < 0) {
      return String(Math.abs(customerSaldo).toFixed(2));
    }
    return String(totalPrice);
  };

  // Pre DEBT/INVOICE/NOT_DELIVERED zablokovať input a nastaviť správnu hodnotu (len pri prvotnom zadávaní)
  useEffect(() => {
    if (isEditMode) return;
    setAmountPaid(defaultAmountForType(paymentType));
  }, [paymentType, totalPrice, customerSaldo, isEditMode]);

  // Zmena druhu platby používateľom musí prepísať aj sumu – aj pri oprave už
  // doručenej objednávky. Inak by po prepnutí "na dlh" → "hotovosť" ostala
  // v poli nula z pôvodnej platby a objednávka by sa uložila znova ako dlh.
  const handlePaymentTypeChange = (value) => {
    setPaymentType(value);
    if (isEditMode && value !== paymentType) {
      setAmountPaid(defaultAmountForType(value));
    }
  };

  const isInputDisabled = paymentType !== "CASH" && paymentType !== "CARD";
  const { balance } = computePaymentFields(paymentType, amountPaid, totalPrice);
  const normalizedDefaultPaymentType = typeof order?.default_payment_type === "string"
    ? order.default_payment_type.trim().toUpperCase()
    : "";
  const basePaymentOptions = isEditMode ? PAYMENT_TYPE_OPTIONS_EDIT : PAYMENT_TYPE_OPTIONS_NEW;
  // Adresa s pevne nastavenou platbou má obmedzený výber aj pri oprave platby –
  // backend iný typ odmietne, takže ho nesmieme ponúknuť ani vodičovi.
  const hasRestrictedPaymentOptions = Boolean(lockPaymentType && normalizedDefaultPaymentType);
  const paymentOptions = hasRestrictedPaymentOptions
    ? basePaymentOptions.filter(({ value }) => (
      value === normalizedDefaultPaymentType || value === "NOT_DELIVERED"
    ))
    : basePaymentOptions;

  return (
    <div className="pf__form">
      <div className="pf__field">
        <Select
          label="Druh platby"
          options={paymentOptions}
          value={paymentType}
          onChange={handlePaymentTypeChange}
          disabled={isSubmitting}
        />
        {lockPaymentType && lockedPaymentTypeHint && (
          <span className="pf__field-hint">{lockedPaymentTypeHint}</span>
        )}
      </div>

      <div className="pf__field">
        <label className="pf__input-label">Zaplatená suma</label>
        <input
          className="pf__input"
          // type="text" + inputMode="decimal", nie type="number": niektore
          // Android klavesnice pisu desatinnu ciarku a type="number" ju v
          // Chrome zahodi -- policko vrati "" a suma by sa poslala ako 0.
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={amountPaid}
          disabled={isInputDisabled || isSubmitting}
          onChange={(e) => setAmountPaid(e.target.value)}
        />
      </div>

      {showDriverNote && !isEditMode && (
        <div className="pf__field">
          <label className="pf__input-label">
            Poznámka vodiča{paymentType === "NOT_DELIVERED" && <span className="pf__label-required"> *</span>}
          </label>
          <textarea
            className="pf__textarea"
            rows={3}
            maxLength={255}
            placeholder={paymentType === "NOT_DELIVERED" ? "Povinné – dôvod nedoručenia..." : "Voliteľná poznámka..."}
            value={driverNote || ""}
            onChange={(e) => setDriverNote(e.target.value)}
            disabled={isSubmitting}
          />
        </div>
      )}

      <PaymentFeedback
        paymentType={paymentType}
        balance={balance}
        amountPaid={amountPaid}
        totalPrice={totalPrice}
        customerSaldo={customerSaldo}
      />
    </div>
  );
}

export default PaymentForm;
